import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { Subject, debounceTime, distinctUntilChanged, switchMap } from 'rxjs';

import { GlobalSearchResult, GlobalSearchService } from '../../services/global-search.service';
import { TranslatePipe } from '../../../core/i18n/translate.pipe';

/** Agent Workspace phase's Global Search Foundation -- the topbar box. Pure UI
 * orchestration: typing drives a debounced query into GlobalSearchService (which owns the
 * actual federation across Tickets/Departments/Queues/Categories), and selecting a result
 * just navigates -- no business rule lives in this component. */
@Component({
  selector: 'app-global-search',
  standalone: true,
  imports: [CommonModule, FormsModule, InputTextModule, TranslatePipe],
  templateUrl: './global-search.component.html',
})
export class GlobalSearchComponent {
  private readonly searchService = inject(GlobalSearchService);
  private readonly router = inject(Router);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly queryChanged = new Subject<string>();

  readonly query = signal('');
  readonly results = signal<GlobalSearchResult[]>([]);
  readonly open = signal(false);
  readonly loading = signal(false);

  constructor() {
    this.queryChanged
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((query) => {
          this.loading.set(true);
          return this.searchService.search(query);
        }),
      )
      .subscribe((results) => {
        this.results.set(results);
        this.loading.set(false);
      });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.open.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.open.set(false);
  }

  onInput(value: string): void {
    this.query.set(value);
    this.open.set(value.trim().length >= 2);
    this.queryChanged.next(value);
  }

  select(result: GlobalSearchResult): void {
    this.open.set(false);
    this.query.set('');
    this.results.set([]);
    void this.router.navigate(result.routerLink);
  }
}
